// Material UI
import { styled, Box, Button, Typography, CircularProgress, Grid } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Card as _Card, CardHeader, CardContent, CardActions } from '@mui/material';
import LaunchIcon from '@mui/icons-material/Launch';

import { useSalesSummary } from 'pages/event/salesSummary/SalesSummaryAPI';
import {
    Incidentals,
    Sales,
    Payments,
    Payouts,
    PromotionsAndComps,
    StripeFeeEstimate,
} from 'pages/event/salesSummary/SalesSummaryReports';

// Components
import { Page, ControlBar, ControlBarHeader } from 'components/ui/layout';
import { SalesSummaryDateRangePicker } from 'pages/event/salesSummary/SalesSummaryDatePicker';
import PayoutHistory from 'components/payouts/PayoutHistory';
import { ActionButton } from 'components/ui/buttons';

// Utility
import moment from 'moment-timezone';
import { useSalesSummaryParams } from './useSalesSummaryParams';

export default function SalesSummary(props) {
    const eventId = props.match.params.id;

    // **** Fetch Sales Summary Data by Date Range **** //

    const { dateRange, setDateRange } = useSalesSummaryParams();
    const { startDate, endDate } = dateRange;

    const summary = useSalesSummary(eventId, startDate, endDate);

    // **** Loading/Error States **** //

    if (summary.dataStatus === 'empty' && summary.fetchStatus === 'fetching') {
        return (
            <Page>
                <Box display="flex" justifyContent="center" alignItems="center" height={'75vh'}>
                    <CircularProgress />
                </Box>
            </Page>
        );
    }

    if (summary.dataStatus === 'error') {
        return (
            <Page>
                <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="center"
                    mt={12}
                >
                    <Typography variant="h5">There was a problem loading your summary</Typography>
                    <Box height={32} />
                    <Button variant="outlined" onClick={() => props.history.goBack()}>
                        Back
                    </Button>
                </Box>
            </Page>
        );
    }

    if (summary.dataStatus !== 'ready') {
        return null;
    }

    // ********** Extract Values ********** //

    const { total_payouts, connect_type_id, bundle_savings, comps } = summary.salesReport;

    const dateRangeApplied = Boolean(startDate && endDate);
    const showPayoutSummary = connect_type_id !== 3 && !dateRangeApplied;

    // Event + Payout Info
    const { cc_fee_order_amt, cc_fee_order_pct } = summary.event;

    const { payouts, connect_id } = summary.payout;

    const { total_incidentals, incidentals } = summary.incidental;

    // ***** Date Display ***** //

    let dateDisplay = 'All Time';

    if (startDate && endDate) {
        const start = moment(startDate);
        const end = moment(endDate);

        if (start.isSame(end, 'day')) {
            dateDisplay = `${start.format('ddd MMM D, YYYY')}`;
        } else if (start.isSame(end, 'year')) {
            dateDisplay = `${start.format('ddd MMM D')} - ${end.format('ddd MMM D, YYYY')}`;
        } else {
            dateDisplay = `${start.format('ddd MMM D, YYYY')} - ${end.format('ddd MMM D, YYYY')}`;
        }
    }

    return (
        <Page>
            <Grid container spacing={2} justifyContent="center">
                <Grid item xs={12} lg={9}>
                    <ControlBar>
                        <ControlBarHeader>Sales & Payout</ControlBarHeader>
                    </ControlBar>
                </Grid>

                <Grid item xs={12} lg={9}>
                    <SalesSummaryDateRangePicker
                        start={startDate}
                        end={endDate}
                        onChange={setDateRange}
                    />
                </Grid>

                <Grid item xs={12} lg={9}>
                    <Card disabled={summary.fetchStatus === 'fetching'}>
                        <CardHeader title="Sales Summary" subheader={dateDisplay} />
                        <CardContent>
                            <Sales {...summary.salesReport} />
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} lg={9}>
                    <Card disabled={summary.fetchStatus === 'fetching'}>
                        <CardHeader title="Payment Summary" subheader={dateDisplay} />
                        <CardContent>
                            <Payments {...summary.salesReport} />
                        </CardContent>
                        {connect_type_id === 3 && connect_id && (
                            <CardActions>
                                <ActionButton
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    href={`https://dashboard.stripe.com/${connect_id}`}
                                    endIcon={<LaunchIcon />}
                                >
                                    Stripe Dashboard
                                </ActionButton>
                            </CardActions>
                        )}
                    </Card>
                    <Box height={12} />

                    {connect_type_id === 3 && (
                        <StripeFeeEstimate
                            cc_fee_order_pct={cc_fee_order_pct}
                            cc_fee_order_amt={cc_fee_order_amt}
                        />
                    )}
                </Grid>

                {showPayoutSummary && (
                    <Grid item xs={12} lg={9}>
                        <Card disabled={summary.fetchStatus === 'fetching'}>
                            <CardHeader title="Payout Summary" subheader="Entire Event" />
                            <CardContent>
                                <Payouts
                                    {...summary.salesReport}
                                    dateRangeApplied={dateRangeApplied}
                                    estimated={Number(summary.event.event_concluded) === 0}
                                />
                                {connect_id ? (
                                    <Box mt={3}>
                                        <Typography variant="body2">
                                            Transactions take up to 3 business days to process.
                                            After which, they will be available to be paid out.
                                        </Typography>
                                    </Box>
                                ) : null}
                            </CardContent>
                        </Card>
                    </Grid>
                )}

                {connect_type_id !== 3 && (
                    <>
                        <Grid item xs={12} lg={9}>
                            <Card disabled={summary.fetchStatus === 'fetching'}>
                                <CardHeader title="Payout History" subheader="Entire Event" />

                                <CardContent>
                                    <PayoutHistory payouts={payouts} totalPayouts={total_payouts} />
                                </CardContent>
                            </Card>
                            <Box height={12} />
                        </Grid>

                        <Grid item xs={12} lg={9}>
                            <Card>
                                <CardHeader
                                    title="Additional Charges & Credits"
                                    subheader="Entire Event"
                                />
                                <CardContent>
                                    <Incidentals
                                        incidentals={incidentals}
                                        total_incidentals={total_incidentals}
                                    />
                                </CardContent>
                            </Card>
                        </Grid>
                    </>
                )}
                {(bundle_savings > 0 || comps > 0) && (
                    <Grid item xs={12} lg={9}>
                        <Card disabled={summary.fetchStatus === 'fetching'}>
                            <CardHeader
                                title="Discounts & Complimentary Tickets"
                                subheader={dateDisplay}
                            />
                            <CardContent>
                                <PromotionsAndComps {...summary.salesReport} />
                            </CardContent>
                        </Card>
                    </Grid>
                )}
            </Grid>
        </Page>
    );
}

const Card = styled(_Card, {
    shouldForwardProp: (prop) => prop !== 'disabled',
})(({ theme, disabled }) => ({
    marginBottom: theme.spacing(2),
    '& .MuiCardContent-root': { paddingTop: 0 },
    color: disabled ? alpha(theme.palette.text.primary, 0.5) : theme.palette.text.primary,
}));
